import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Email as EmailIcon,
  Inventory2Outlined as ArchiveIcon,
  PersonOff as PersonOffIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  StarRate as StarRateIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import actorApi from '../../api/actorApi';
import T, { useAutoTranslate } from '../common/T';

const buildDisplayName = (actor, fallback = 'Client') => {
  if (!actor) return fallback;
  if (actor.type === 'enterprise') return actor.name || fallback;
  return `${actor.first_name || ''} ${actor.last_name || ''}`.trim() || fallback;
};

const buildAddressLine = (address) => {
  if (!address) return '';
  return [
    address.street,
    [address.postal_code, address.city].filter(Boolean).join(' '),
    address.state,
    address.country,
  ].filter(Boolean).join(', ');
};

const parseUtc = (value) => {
  if (!value) return null;
  const text = String(value);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatZurichDate = (value) => {
  const date = parseUtc(value);
  return date
    ? date.toLocaleString('de-CH', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';
};

const ActorQuickInfoDialog = ({ open, actorId, fallbackName = 'Client', onClose, onActorSaved }) => {
  const at = useAutoTranslate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [actor, setActor] = useState(null);
  const [form, setForm] = useState({
    type: 'individual',
    first_name: '',
    last_name: '',
    name: '',
    email: '',
    phone: '',
    website: '',
    preferred_language: 'de',
    gender: '',
    preferred_language_manual: false,
    gender_manual: false,
    rating: '',
    rating_note: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // 'missing': the contact no longer exists (removed before archiving existed).
  // 'unavailable': the contact service did not answer; the user can retry.
  const [loadState, setLoadState] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [restoring, setRestoring] = useState(false);
  const [pointDraft, setPointDraft] = useState({ kind: 'phone', value: '', label: '' });
  const [pointBusy, setPointBusy] = useState(false);
  const [pointError, setPointError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!open || !actorId) return;

    let active = true;
    const loadActor = async () => {
      setLoading(true);
      setError('');
      setLoadState('');
      try {
        const data = await actorApi.getActor(actorId);
        if (!active) return;
        setActor(data);
        setForm({
          type: data?.type || 'individual',
          first_name: data?.first_name || '',
          last_name: data?.last_name || '',
          name: data?.name || '',
          email: data?.email || '',
          phone: data?.phone || '',
          website: data?.website || '',
          preferred_language: data?.preferred_language || 'de',
          gender: data?.gender || '',
          preferred_language_manual: Boolean(data?.preferred_language_manual),
          gender_manual: Boolean(data?.gender_manual),
          rating: data?.rating ?? '',
          rating_note: data?.rating_note || '',
        });
      } catch (err) {
        console.error('Failed to load actor info:', err);
        if (!active) return;
        setActor(null);
        setLoadState(err?.response?.status === 404 ? 'missing' : 'unavailable');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadActor();
    return () => { active = false; };
  }, [open, actorId, reloadKey]);

  const handleRetry = () => {
    if (typeof actorApi.getActor.clearCache === 'function') actorApi.getActor.clearCache();
    setReloadKey((key) => key + 1);
  };

  const contactPoints = Array.isArray(actor?.contact_points) ? actor.contact_points : [];

  const handleAddPoint = async () => {
    const value = pointDraft.value.trim();
    if (!actorId || !value) return;
    setPointBusy(true);
    setPointError('');
    try {
      const result = await actorApi.addContactPoint(actorId, { ...pointDraft, value });
      if (result?.already_primary) {
        setPointError('This is already the main phone number or e-mail of the contact.');
      } else if (result?.item) {
        setActor((prev) => (prev ? {
          ...prev,
          contact_points: [...(prev.contact_points || []).filter((point) => point.id !== result.item.id), result.item],
        } : prev));
        setPointDraft((draft) => ({ ...draft, value: '', label: '' }));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPointError(typeof detail === 'string' ? detail : 'This phone number or e-mail could not be added.');
    } finally {
      setPointBusy(false);
    }
  };

  const handleRemovePoint = async (point) => {
    setPointBusy(true);
    setPointError('');
    try {
      await actorApi.deleteContactPoint(actorId, point.id);
      setActor((prev) => (prev ? { ...prev, contact_points: (prev.contact_points || []).filter((item) => item.id !== point.id) } : prev));
    } catch (err) {
      setPointError('This entry could not be removed.');
    } finally {
      setPointBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!actorId) return;
    setRestoring(true);
    setError('');
    try {
      const restored = await actorApi.restoreActor(actorId);
      setActor((prev) => (prev ? { ...prev, ...restored } : prev));
      setSuccessMessage('Contact restored');
      if (typeof onActorSaved === 'function' && actor) onActorSaved({ ...actor, ...restored });
    } catch (err) {
      setError(err?.response?.data?.detail || 'The contact could not be restored.');
    } finally {
      setRestoring(false);
    }
  };

  const displayName = useMemo(() => buildDisplayName(actor, fallbackName), [actor, fallbackName]);
  const initials = displayName
    .replace(/^Client\s*/i, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'CL';
  const defaultAddress = actor?.addresses?.find((address) => address.is_default) || actor?.addresses?.[0];
  const addressLine = buildAddressLine(defaultAddress);
  const isEnterprise = form.type === 'enterprise';

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(['preferred_language', 'gender'].includes(field) ? { [`${field}_manual`]: true } : {}),
    }));
  };

  const validate = () => {
    if (!actorId) return 'Actor link is missing.';
    if (!form.email.trim()) return 'Email is required.';
    if (!form.phone.trim()) return 'Phone is required.';
    if (isEnterprise && !form.name.trim()) return 'Company name is required.';
    if (!isEnterprise && !form.first_name.trim()) return 'First name is required.';
    if (!isEnterprise && !form.last_name.trim()) return 'Last name is required.';
    if (form.rating !== '') {
      const value = Number(form.rating);
      if (!Number.isInteger(value) || value < 1 || value > 10) {
        return 'Rating must be a whole number from 1 to 10.';
      }
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        type: form.type,
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        correspondence_method: actor?.correspondence_method || 'E-Mail',
        rating: form.rating === '' ? null : parseInt(form.rating, 10),
        rating_note: form.rating_note.trim() || null,
      };

      for (const field of ['preferred_language', ...(!isEnterprise ? ['gender'] : [])]) {
        const marker = `${field}_manual`;
        if (form[field] !== (actor?.[field] || (field === 'preferred_language' ? 'de' : '')) ||
            form[marker] !== Boolean(actor?.[marker])) {
          payload[field] = form[field] || null;
          payload[marker] = form[marker];
        }
      }

      if (isEnterprise) {
        payload.name = form.name.trim();
      } else {
        payload.first_name = form.first_name.trim();
        payload.last_name = form.last_name.trim();
      }

      const saved = await actorApi.updateActor(actorId, payload);
      setActor(saved);
      if (typeof actorApi.getActor.clearCache === 'function') actorApi.getActor.clearCache();
      if (typeof actorApi.getActors.clearCache === 'function') actorApi.getActors.clearCache();
      if (typeof actorApi.getActorsByIds.clearCache === 'function') actorApi.getActorsByIds.clearCache();
      if (typeof onActorSaved === 'function') onActorSaved(saved);
      setSuccessMessage('Actor saved successfully');
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      console.error('Failed to save actor info:', err);
      setError(err?.response?.data?.detail || err?.message || 'Could not save actor information.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '8px' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Avatar
            src={actor?.profile_picture_url && actor?.id ? actorApi.getProfilePictureUrl(actor.id) : undefined}
            sx={{
              width: 42,
              height: 42,
              bgcolor: isEnterprise ? '#ede9fe' : '#dcfce7',
              color: isEnterprise ? '#7c3aed' : '#15803d',
              fontWeight: 900,
              fontSize: '0.9rem',
            }}
          >
            {!actor?.profile_picture_url && (isEnterprise ? <BusinessIcon fontSize="small" /> : initials)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }} noWrap>
              {displayName}
            </Typography>
            <Stack direction="row" spacing={0.6} sx={{ mt: 0.4 }}>
              <Chip
                icon={isEnterprise ? <BusinessIcon sx={{ fontSize: 13 }} /> : <PersonIcon sx={{ fontSize: 13 }} />}
                label={isEnterprise ? 'Enterprise' : 'Individual'}
                size="small"
                sx={{ height: 22, fontWeight: 700, bgcolor: '#f8fafc' }}
              />
              {actor?.archived && (
                <Chip icon={<ArchiveIcon sx={{ fontSize: 13 }} />} label={at("Archived")} size="small" sx={{ height: 22, fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569' }} />
              )}
            </Stack>
          </Box>
        </Stack>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#f8fafc', p: { xs: 1.5, sm: 2 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}><T>{error}</T></Alert>}

        {loading ? (
          <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#32cd32' }} />
          </Box>
        ) : loadState ? (
          <Stack alignItems="center" spacing={1.25} sx={{ minHeight: 240, justifyContent: 'center', textAlign: 'center', px: 2 }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 52, height: 52, borderRadius: '50%', bgcolor: loadState === 'missing' ? '#f1f5f9' : '#fef3c7', color: loadState === 'missing' ? '#475569' : '#b45309' }}>
              {loadState === 'missing' ? <PersonOffIcon /> : <RefreshIcon />}
            </Box>
            <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
              {loadState === 'missing' ? 'Contact no longer in Actor Management' : 'Contact details are unavailable right now'}
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: '#475569', maxWidth: 400 }}>
              {loadState === 'missing'
                ? 'This contact was removed before contacts were archived instead of deleted. The conversation and all its messages are kept. Search the customer by phone or e-mail to link the conversation to a contact again.'
                : 'The contact service did not answer. The conversation is not affected. Try again in a moment.'}
            </Typography>
            {loadState === 'unavailable' && (
              <Button onClick={handleRetry} startIcon={<RefreshIcon />} variant="outlined" sx={{ textTransform: 'none', fontWeight: 800, borderColor: '#bbf7d0', color: '#15803d' }}><T>
                Try again
              </T></Button>
            )}
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            {actor?.archived && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #cbd5e1' }}>
                <ArchiveIcon sx={{ color: '#475569' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: 13.5, color: '#1e293b' }}><T>This contact is archived</T></Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
                    {[actor.archived_at && `Since ${formatZurichDate(actor.archived_at)}`, actor.archived_by && `by ${actor.archived_by}`].filter(Boolean).join(' ')}
                    {actor.archived_at ? '. ' : ''}<T>Messages and history stay available.
                  </T></Typography>
                </Box>
                <Button
                  size="small"
                  onClick={handleRestore}
                  disabled={restoring}
                  startIcon={restoring ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
                  sx={{ textTransform: 'none', fontWeight: 800, color: '#15803d', flexShrink: 0 }}
                ><T>
                  Restore
                </T></Button>
              </Box>
            )}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: isEnterprise ? '1fr' : '1fr 1fr' },
              gap: 1.25,
              p: 1.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              {isEnterprise ? (
                <TextField label={at("Company name")} size="small" value={form.name} onChange={(e) => updateField('name', e.target.value)} />
              ) : (
                <>
                  <TextField label={at("First name")} size="small" value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} />
                  <TextField label={at("Last name")} size="small" value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} />
                </>
              )}
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.25,
              p: 1.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              <TextField
                label={at("Email")}
                size="small"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                InputProps={{ startAdornment: <EmailIcon sx={{ mr: 0.8, fontSize: 18, color: '#64748b' }} /> }}
              />
              <TextField
                label={at("Phone")}
                size="small"
                value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 0.8, fontSize: 18, color: '#64748b' }} /> }}
              />
              <TextField
                select
                label={at("Preferred language")}
                size="small"
                value={form.preferred_language}
                onChange={(e) => updateField('preferred_language', e.target.value)}
              >
                <MenuItem value="de"><T>Deutsch</T></MenuItem>
                <MenuItem value="fr"><T>Français</T></MenuItem>
                <MenuItem value="it"><T>Italiano</T></MenuItem>
                <MenuItem value="en"><T>English</T></MenuItem>
              </TextField>
              {!isEnterprise && (
                <TextField
                  select
                  label={at("Gender / form of address")}
                  size="small"
                  value={form.gender}
                  onChange={(e) => updateField('gender', e.target.value)}
                >
                  <MenuItem value=""><T>Unknown / neutral</T></MenuItem>
                  <MenuItem value="F"><T>Female / Ms</T></MenuItem>
                  <MenuItem value="M"><T>Male / Mr</T></MenuItem>
                  <MenuItem value="Other"><T>Other</T></MenuItem>
                </TextField>
              )}
              <FormControlLabel
                control={<Switch checked={!form.preferred_language_manual} onChange={(e) => updateField('preferred_language_manual', !e.target.checked)} />}
                label={at("Detect language automatically")}
              />
              {!isEnterprise && (
                <FormControlLabel
                  control={<Switch checked={!form.gender_manual} onChange={(e) => updateField('gender_manual', !e.target.checked)} />}
                  label={at("Use explicit customer statements")}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}><T>
                Manual changes take priority. Gender is only taken from an explicit customer statement.
              </T></Typography>
              <TextField
                label={at("Website")}
                size="small"
                value={form.website}
                onChange={(e) => updateField('website', e.target.value)}
                InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 0.8, fontSize: 18, color: '#64748b' }} /> }}
                sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
              />
            </Box>

            <Box sx={{
              p: 1.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              <Typography sx={{ fontWeight: 900, mb: 0.25 }}><T>Other phone numbers and e-mails</T></Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}><T>
                Messages from these are recognised as this contact.
              </T></Typography>
              {contactPoints.length > 0 && (
                <Stack spacing={0.75} sx={{ mb: 1.25 }}>
                  {contactPoints.map((point) => (
                    <Stack key={point.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.6, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #eef2f7' }}>
                      {point.kind === 'email' ? <EmailIcon sx={{ fontSize: 17, color: '#64748b' }} /> : <PhoneIcon sx={{ fontSize: 17, color: '#64748b' }} />}
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{point.value}</Typography>
                      {point.label && <Chip label={point.label} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
                      <IconButton size="small" aria-label={`Remove ${point.value}`} disabled={pointBusy} onClick={() => handleRemovePoint(point)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  select
                  size="small"
                  label={at("Type")}
                  value={pointDraft.kind}
                  onChange={(e) => setPointDraft((draft) => ({ ...draft, kind: e.target.value }))}
                  sx={{ width: { xs: '100%', sm: 120 } }}
                >
                  <MenuItem value="phone"><T>Phone</T></MenuItem>
                  <MenuItem value="email"><T>E-mail</T></MenuItem>
                </TextField>
                <TextField
                  size="small"
                  label={pointDraft.kind === 'email' ? 'E-mail address' : 'Phone number'}
                  value={pointDraft.value}
                  onChange={(e) => setPointDraft((draft) => ({ ...draft, value: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPoint(); } }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label={at("Label")}
                  placeholder={at("Work, partner…")}
                  value={pointDraft.label}
                  onChange={(e) => setPointDraft((draft) => ({ ...draft, label: e.target.value }))}
                  sx={{ width: { xs: '100%', sm: 130 } }}
                />
                <Button
                  onClick={handleAddPoint}
                  disabled={pointBusy || !pointDraft.value.trim() || !actorId}
                  startIcon={pointBusy ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                  sx={{ textTransform: 'none', fontWeight: 800, color: '#15803d', whiteSpace: 'nowrap' }}
                ><T>
                  Add
                </T></Button>
              </Stack>
              {pointError && <Typography sx={{ mt: 0.75, fontSize: 12.5, color: '#b91c1c' }}><T>{pointError}</T></Typography>}
            </Box>

            <Box sx={{
              p: 1.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <StarRateIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 900 }}><T>Actor Rating</T></Typography>
                </Stack>
                <Box sx={{
                  px: 1.2,
                  py: 0.3,
                  borderRadius: 999,
                  bgcolor: form.rating ? '#dcfce7' : '#f1f5f9',
                  color: form.rating ? '#047857' : '#64748b',
                  fontWeight: 900,
                  fontSize: '0.8rem',
                }}>
                  {form.rating || '-'} / 10
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <TextField
                  label={at("Rating")}
                  type="number"
                  size="small"
                  value={form.rating}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 10)) {
                      updateField('rating', value);
                    }
                  }}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  sx={{ width: { xs: '100%', sm: 128 } }}
                />
                <TextField
                  label={at("Rating note")}
                  size="small"
                  multiline
                  minRows={3}
                  value={form.rating_note}
                  onChange={(e) => updateField('rating_note', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>

            <Box sx={{
              p: 1.5,
              bgcolor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <LocationOnIcon sx={{ fontSize: 19, color: '#64748b', mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 13, color: '#334155' }}><T>Address</T></Typography>
                  <Typography sx={{ fontSize: 13, color: addressLine ? '#475569' : '#94a3b8' }}>
                    {addressLine || 'No address'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: { xs: 1.25, sm: 1.5 }, gap: 1, flexShrink: 0 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}><T>
          Cancel
        </T></Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading || !actorId || Boolean(loadState)}
          sx={{ textTransform: 'none', fontWeight: 900, bgcolor: '#32cd32', '&:hover': { bgcolor: '#28a428' } }}
        >
          {saving ? 'Saving...' : 'Save Actor'}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={Boolean(successMessage)}
      autoHideDuration={2600}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="success"
        variant="filled"
        onClose={() => setSuccessMessage('')}
        sx={{ fontWeight: 800, borderRadius: '8px' }}
      >
        <T>{successMessage}</T>
      </Alert>
    </Snackbar>
    </>
  );
};

ActorQuickInfoDialog.propTypes = {
  open: PropTypes.bool,
  actorId: PropTypes.string,
  fallbackName: PropTypes.string,
  onClose: PropTypes.func,
  onActorSaved: PropTypes.func,
};

export default ActorQuickInfoDialog;
